import type { ComplianceSummary } from '@bartizo/shared';

/**
 * Where a server's numbers are on their way from a fix to the page (P11.19). Only
 * what the page already knows: when the last compliance fixes finished, the newest
 * scan the API stored, and when it last read the manager.
 *
 * A scan counts as coming after the fixes when it finished no more than two minutes
 * before the command did: the helper restarts the Wazuh agent, which scans again
 * within seconds, and only then does the command report, so the scan can end a
 * little before the fixes' own completion time.
 */

const SCAN_SLACK_MS = 2 * 60 * 1000;
/** A scan takes seconds and the API reads every ten minutes: this long without one is wrong. */
const WAITED_TOO_LONG_MS = 30 * 60 * 1000;

export type CompliancePhase =
  /** Nothing has been applied here: the scan and the read are all there is to say. */
  | 'idle'
  /** Fixes ran, and no scan since is stored yet. */
  | 'waiting'
  /** Waiting, and far longer than a scan and a read take. */
  | 'stalled'
  /** The newest scan came after the last fixes. */
  | 'current';

export interface ComplianceProgress {
  phase: CompliancePhase;
  /** The next scheduled read, while waiting and the read time is known. */
  nextReadAt: string | null;
}

export function complianceProgress(
  summary: Pick<ComplianceSummary, 'scannedAt' | 'status'>,
  now: Date = new Date(),
): ComplianceProgress {
  const { lastApply, readAt, readEverySeconds } = summary.status;
  if (lastApply === null) return { phase: 'idle', nextReadAt: null };

  const appliedAt = Date.parse(lastApply.at);
  const scannedAt = summary.scannedAt === null ? null : Date.parse(summary.scannedAt);
  if (scannedAt !== null && scannedAt >= appliedAt - SCAN_SLACK_MS) {
    return { phase: 'current', nextReadAt: null };
  }

  const nextReadAt =
    readAt === null ? null : new Date(Date.parse(readAt) + readEverySeconds * 1000).toISOString();
  return {
    phase: now.getTime() - appliedAt > WAITED_TOO_LONG_MS ? 'stalled' : 'waiting',
    nextReadAt,
  };
}
